import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Bell,
  Loader2,
  AlertTriangle,
  ShieldAlert,
  // ShieldXIcon,
} from "lucide-react";
import { ALERT_ALL_MAX_HOURS } from "../constants.js";

import { useExpirationAlerts } from "./useExpirationAlerts.jsx";

const ONE_HOUR_MS = 60 * 60 * 1000;

// Reuses the same formatter for all alert dates.
const alertDateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

// Formats dates using the British day/month/year format.
const formatDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "-";
  }

  return alertDateFormatter.format(date);
};

// Returns the status color based on the expiration status.
const getStatusColor = (hours, isExpired) => {
  // Keep the expired status color available for future use.

  if (hours <= ALERT_ALL_MAX_HOURS) return "text-amber-600";

  return "text-stone-500";
};

// Calculates the remaining status using the fixed expiration date.
const getRemainingTime = (expiryDate, isExpired) => {
  if (!(expiryDate instanceof Date) || Number.isNaN(expiryDate.getTime())) {
    return {
      hours: 0,
      text: "-",
      isExpired: false,
    };
  }

  const now = new Date();

  const remainingMilliseconds = expiryDate.getTime() - now.getTime();

  const remainingHours = Math.max(
    0,
    Math.floor(remainingMilliseconds / ONE_HOUR_MS),
  );

  const today = new Date();
  const targetDate = new Date(expiryDate);

  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  if (targetDate.getTime() === today.getTime()) {
    return {
      hours: remainingHours,
      text: "Today",
      isExpired: false,
    };
  }

  const daysLeft = Math.ceil(
    (targetDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000),
  );

  return {
    hours: remainingHours,
    text: `${daysLeft}d left`,
    isExpired: false,
  };
};

// Checks whether an alert belongs to the selected status filter.
const matchesAlertFilter = (alert, filter) => {
  const expiryDate = alert?.expiryDate;

  if (!(expiryDate instanceof Date) || Number.isNaN(expiryDate.getTime())) {
    return false;
  }

  const now = Date.now();
  const expiryTime = expiryDate.getTime();

  // Uses the expiration status already calculated from Device Settings.
  const isExpired = alert?.isExpired === true;

  // Shows only active settings that will expire soon.
  if (filter === "expires") {
    return !isExpired && expiryTime >= now;
  }

  // Shows both upcoming and recently expired settings.
  if (filter === "all") {
    return true;
  }

  return false;
};

// Renders a single alert item.
const AlertItem = ({ alert, copiedId, onCopy }) => {
  // Builds the countdown text and expiration status from the API remaining hours.
  const { hours, text, isExpired } = getRemainingTime(
    alert.expiryDate,
    alert.isExpired,
  );

  const statusColor = getStatusColor(hours, isExpired);

  return (
    <li className="flex items-start gap-3 p-3 rounded-md hover:bg-stone-50">
      <div className={`flex-shrink-0 mt-1 ${statusColor}`}>
        {/* Keep the expired icon logic available for future use. */}

        <ShieldAlert className="w-5 h-5" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onCopy(alert.id, alert.deviceName)}
            title="Click to copy name"
            className="min-w-0 text-sm font-medium text-left truncate text-stone-900 hover:text-blue-600"
          >
            {alert.deviceName}
          </button>

          {copiedId === alert.id && (
            <span className="flex-shrink-0 text-xs font-medium text-green-600">
              Copied!
            </span>
          )}
        </div>

        <div className="text-xs truncate text-stone-500">{alert.shopName}</div>

        {/* Displays the setting associated with this alert. */}
        <div className="text-xs font-medium text-stone-700">
          {alert.settingName}
        </div>

        <div className="mt-2 space-y-0.5">
          <div className="text-xs text-stone-400">
            Created: {formatDate(alert.createdAt)}
          </div>

          <div className="text-xs text-stone-400">
            Expires: {formatDate(alert.expiryDate)}
          </div>
        </div>
      </div>

      <div
        className={`mt-1 text-sm font-medium ${statusColor} flex-shrink-0 whitespace-nowrap`}
      >
        {text}
      </div>
    </li>
  );
};

const Alerts = ({
  onClose,
  initialFailedControllerIds = [],
  initialRefreshing = false,
}) => {
  // References the modal container to detect outside clicks.
  const containerRef = useRef(null);

  // Stores the timeout used to clear the copy feedback.
  const copyTimeoutRef = useRef(null);

  // Loads the shared validated alerts used by both the popup and top bar.
  const { alerts, isPending, error } = useExpirationAlerts();

  // Stores the device IDs that failed during the last refresh.
  const [failedControllerIds, setFailedControllerIds] = useState(
    initialFailedControllerIds,
  );

  // Keeps the local failed controller list synchronized with the TopBar refresh.
  useEffect(() => {
    setFailedControllerIds(initialFailedControllerIds);
  }, [initialFailedControllerIds]);

  // Stores the ID of the alert whose device name was copied.
  const [copiedId, setCopiedId] = useState(null);

  // Stores the selected alert filter.
  const [alertFilter, setAlertFilter] = useState("expires");

  // Stores the current alert search term.
  const [searchTerm, setSearchTerm] = useState("");

  // Sorts alerts using the real expiration date from Device Settings.
  const sortedAlerts = useMemo(() => {
    return [...alerts].sort((alertA, alertB) => {
      const alertAExpired = alertA?.isExpired === true;
      const alertBExpired = alertB?.isExpired === true;

      // Keeps active alerts before expired alerts.
      if (alertAExpired !== alertBExpired) {
        return alertAExpired ? 1 : -1;
      }

      const alertAExpiryTime = alertA?.expiryDate?.getTime?.() ?? 0;
      const alertBExpiryTime = alertB?.expiryDate?.getTime?.() ?? 0;

      // Sorts active alerts from the nearest expiration to the farthest.
      if (!alertAExpired && !alertBExpired) {
        return alertAExpiryTime - alertBExpiryTime;
      }

      // Sorts expired alerts from the most recent expiration to the oldest.
      return alertBExpiryTime - alertAExpiryTime;
    });
  }, [alerts]);

  // Filters alerts by search term and expiration status.
  const filteredAlerts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    // Checks whether the alert matches the current search text.
    const matchesSearch = (alert) => {
      if (!normalizedSearch) {
        return true;
      }

      return (
        alert.deviceId.toLowerCase().includes(normalizedSearch) ||
        alert.shopName.toLowerCase().includes(normalizedSearch) ||
        alert.deviceName.toLowerCase().includes(normalizedSearch)
      );
    };

    return sortedAlerts.filter((alert) => {
      // Hides alerts from controllers that failed during the last refresh.
      // Normalizes the alert device ID before comparing refresh failures.
      const alertDeviceId = String(alert?.deviceId ?? "")
        .trim()
        .toLowerCase();

      // Checks whether the controller failed during the latest refresh.
      const hasRefreshError = failedControllerIds.some(
        (deviceId) =>
          String(deviceId ?? "")
            .trim()
            .toLowerCase() === alertDeviceId,
      );

      if (hasRefreshError) {
        return false;
      }

      // Skips alerts that do not match the current search text.
      if (!matchesSearch(alert)) {
        return false;
      }

      // Applies the selected alert status filter.
      return matchesAlertFilter(alert, alertFilter);
    });
  }, [sortedAlerts, alertFilter, searchTerm, failedControllerIds]);

  // Closes the alerts popup.
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Copies the device name to the clipboard.
  const handleCopy = useCallback((alertId, deviceName) => {
    navigator.clipboard.writeText(deviceName);

    setCopiedId(alertId);

    // Clears the copy feedback after a short delay.
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }

    copyTimeoutRef.current = setTimeout(() => {
      setCopiedId(null);
    }, 1500);
  }, []);

  // Closes the popup when the user clicks outside of it.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClose]);

  // Clears the copy feedback timeout when the component is unmounted.
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-end p-4 pt-24"
      role="dialog"
      aria-modal="true"
      aria-labelledby="alerts-title"
    >
      <div className="fixed inset-0 bg-stone-900/20" aria-hidden="true" />

      <div
        ref={containerRef}
        className="relative z-10 w-full max-w-md overflow-hidden bg-white shadow-2xl rounded-xl ring-1 ring-stone-200"
      >
        <div className="flex items-center gap-3 p-4 border-b border-stone-100 bg-stone-50">
          <div className="relative flex items-center justify-center w-8 h-8 border rounded-full text-stone-700 bg-stone-100 border-stone-200">
            <Bell className="w-4 h-4" />
          </div>

          <h3
            id="alerts-title"
            className="text-base font-semibold text-stone-900"
          >
            Expiring Settings
          </h3>

          <button
            type="button"
            onClick={handleClose}
            className="ml-auto px-3 py-1.5 text-sm border rounded-md bg-stone-100 border-stone-300 text-stone-700 hover:bg-stone-200"
          >
            Close
          </button>
        </div>

        <div className="flex items-center gap-3 p-4 border-b border-stone-100 bg-stone-50">
          {/* Searches alerts by device ID, shop name, or SSID. */}
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search device ID, shop, or SSID..."
            className="w-56 px-2 py-1 text-sm bg-white border rounded-md border-stone-200 text-stone-700"
          />

          {/* Clears the search field. */}
          <button
            type="button"
            onClick={() => setSearchTerm("")}
            className="px-3 py-1 text-sm border rounded-md bg-stone-100 border-stone-300 text-stone-700 hover:bg-stone-200"
          >
            Clear
          </button>
        </div>

        <div className="p-2 max-h-[60vh] overflow-auto">
          {isPending || initialRefreshing ? (
            <div className="flex flex-col items-center justify-center p-8 text-sm text-stone-500">
              <Loader2 className="w-5 h-5 mb-2 animate-spin" />
              Refreshing alerts...
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center p-8 text-sm text-red-600">
              <AlertTriangle className="w-5 h-5 mb-2" />
              Error loading alerts.
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="p-8 text-sm text-center text-stone-500">
              No alerts found.
            </div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {filteredAlerts.map((alert) => (
                <AlertItem
                  key={alert.id}
                  alert={alert}
                  copiedId={copiedId}
                  onCopy={handleCopy}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-stone-100 bg-stone-50">
          <div className="text-sm text-stone-600">
            {isPending || initialRefreshing
              ? "Refreshing..."
              : `${filteredAlerts.length} ${
                  filteredAlerts.length === 1 ? "alert" : "alerts"
                }`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Alerts;
